import { Box, Button, Card, CardContent, IconButton, Skeleton, Stack, Tooltip } from "@mui/material";
import Iconify from "../../../minimals/components/iconify";
import { useEffect, useState } from "react";
import { StyledBadge } from "../../shared/StyledBadge";
import OrderNotesModal from "../notes/OrderNotesModal";
import { LedgerTransaction, TaskGroupEntity } from "../types";
import SpreadAmount from "./SpreadAmount";
import Properties from "./Properties";
import Order from "./Order";
import ClosingChecklistLedgers from "../ClosingChecklistLedgers";
import ClosingChecklistEscrow from "../ClosingChecklistEscrow";
import { EscrowLedger } from "../../reports/escrowLedgers/types";
import axios from "axios";
import LedgerSummary from "../LedgerSummary";
import { processLedgerData } from "../../reports/escrowLedgers/processLedgerData";
import { useQueryParam } from "use-query-params";
import LedgerLoader from "./LedgerLoader";
import UploadModal from "../../shared/UploadModal";
import GroupEntityDocumentsModal from "./GroupEntityDocumentsModal";

const TaskOrderInfo = ({
  taskGroupEntity,
  refreshTaskGroupEntity
}: {
  taskGroupEntity: TaskGroupEntity | null;
  refreshTaskGroupEntity: () => Promise<void>;
}) => {
  const [orderNumber] = useQueryParam<string>("order");
  const [addOrderNoteMode, setAddOrderNoteMode] = useState<boolean>(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState<boolean>(false);
  const [isViewEntityDocumentsModalOpen, setIsViewEntityDocumentsModalOpen] = useState<boolean>(false);
  const [openClosingChecklistEscrow, setOpenClosingChecklistEscrow] = useState<boolean>(false);
  const [escrowLedgers, setEscrowLedgers] = useState<LedgerTransaction[]>([]);
  const [escrowLedgerTotal, setEscrowLedgerTotal] = useState<number | null>(null);
  const [isLedgersLoading, setIsLedgersLoading] = useState<boolean>(true);

  const isFlips = taskGroupEntity?.applicationId === 1;
  const isClosingChecklist = taskGroupEntity?.applicationId === 2;

  const handleDocumentsSave = async (documentIds: number[]) => {
    if (!taskGroupEntity?.id || !documentIds?.length) return;
    await axios.post("/api/taskmanagement/taskGroupEntity/addMomentumDocuments", {
      momentumDocumentIds: documentIds,
      taskGroupEntityId: taskGroupEntity.id
    });

    refreshTaskGroupEntity();
  };

  const fetchEscrowLedgers = async () => {
    setIsLedgersLoading(true);
    if (!orderNumber) {
      return;
    }
    const { data } = await axios.get<EscrowLedger[]>(`/proxy/api/ledgers/getescrowledgers`);
    const ledgers = processLedgerData(data);
    const transactions = ledgers
      .filter((d) => d.name.startsWith(orderNumber))
      .map((d) => {
        const splitName = d.name.split("-");
        return {
          name: splitName[splitName.length - 1],
          amount: d.amount
        };
      })
      .sort((a, b) => {
        const numA = parseInt(a.name.replace(/\D/g, ""), 10);
        const numB = parseInt(b.name.replace(/\D/g, ""), 10);
        return numA - numB;
      }) as LedgerTransaction[];
    setEscrowLedgers(transactions);
    const escrowTotal = transactions.reduce((total, transaction) => {
      return total + transaction.amount;
    }, 0);
    setEscrowLedgerTotal(escrowTotal);
    setIsLedgersLoading(false);
  };

  useEffect(() => {
    fetchEscrowLedgers();
  }, [orderNumber]);

  return (
    <>
      <Stack direction="row" marginBottom={3}>
        <Card sx={{ width: "100%", padding: 0 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={14}>
                <Order />
                <Properties />
                {isFlips && <SpreadAmount taskGroupEntity={taskGroupEntity} />}
                <Stack direction="row" spacing={2}>
                  {isClosingChecklist && (
                    <>
                      <ClosingChecklistLedgers
                        escrowContent={(
                          <Box>
                            <Button
                              variant="outlined"
                              onClick={() => setOpenClosingChecklistEscrow(true)}
                              size="small"
                              sx={{ marginBottom: 1 }}
                            >
                              Transfer Escrow Funds
                            </Button>
                            {openClosingChecklistEscrow && (
                              <ClosingChecklistEscrow
                                onClose={() => setOpenClosingChecklistEscrow(false)}
                                refresh={fetchEscrowLedgers}
                              />
                            )}
                            {!isLedgersLoading && escrowLedgers.length > 0 && (
                              <LedgerSummary
                                ledgerTypeHeader={"Escrow Ledgers"}
                                total={escrowLedgerTotal}
                                transactions={escrowLedgers}
                                orderNumber={orderNumber}
                              />
                            )}
                            {isLedgersLoading && <LedgerLoader />}
                          </Box>
                        )}
                      />
                    </>
                  )}
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1}>
                {taskGroupEntity && (
                  <>
                    <Box>
                      <Tooltip title="View Documents">
                        <IconButton onClick={() => setIsViewEntityDocumentsModalOpen(true)}>
                          <StyledBadge badgeContent={taskGroupEntity?.documents.length} color="info">
                            <Iconify icon="iconamoon:folder-document" />
                          </StyledBadge>
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box>
                      <Tooltip title="Upload Documents">
                        <IconButton onClick={() => setIsAddDocumentModalOpen(true)}>
                          <Iconify icon="eva:upload-fill" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box>
                      <Tooltip title="Add Notes">
                        <IconButton onClick={() => setAddOrderNoteMode(true)}>
                          <StyledBadge badgeContent={taskGroupEntity?.notes?.length} color="error">
                            <Iconify icon="eva:file-add-outline" />
                          </StyledBadge>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                )}
                {!taskGroupEntity && <Skeleton variant="circular" width={36} height={36} />}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      {addOrderNoteMode && (
        <OrderNotesModal
          taskGroupEntity={taskGroupEntity}
          closeModal={() => setAddOrderNoteMode(false)}
          refresh={refreshTaskGroupEntity}
        />
      )}

      {isAddDocumentModalOpen && (
        <UploadModal
          open={isAddDocumentModalOpen}
          handleClose={() => {
            setIsAddDocumentModalOpen(false);
          }}
          updateItems={() => {}}
          onSave={handleDocumentsSave}
          documentInfo={{} as any}
          orderNumber={orderNumber}
          hasDocumentType
          hasAdditionalDescription
          isUseNameOfFile
          applicationInitiator="TitleQ.TaskManagement"
        />
      )}
      {isViewEntityDocumentsModalOpen && (
        <GroupEntityDocumentsModal
          taskGroupEntity={taskGroupEntity}
          handleClose={() => {
            setIsViewEntityDocumentsModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default TaskOrderInfo;
